<script>
import { Chart, ArcElement, Tooltip, Legend, PieController } from 'chart.js';
import VoteQuestion from './VoteQuestion.vue';
import VoteRating from './VoteRating.vue';
Chart.register(ArcElement, Tooltip, Legend, PieController);

export default {
  name: "SurveyPoll",
  components: { VoteQuestion, VoteRating },
  props: {
    slug: { type: String, required: true },
  },
  data() {
    return {
      type_rating: 9,
      loading: true,
      error: "",
      survey: null,
      hasVoted: false,
      allowRevoting: false,
      revoting: false,

      questions: [],
      answers: {},

      results: null,
      charts: {},
      colorCache: {},
    };
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    // -------- URLs --------
    surveyUrl() { return `/surveys/${this.slug}/`; },
    schemaUrl() { return `/surveys/${this.slug}/schema/`; },
    resultsUrl() { return `/surveys/${this.slug}/results/`; },
    voteUrl() { return `/surveys/${this.slug}/vote/`; },

    // -------- Flow --------
    async bootstrap() {
      try {
        await this.fetchSurvey();
        await this.fetchSchema();
        if (this.hasVoted) {
          await this.fetchResults();
        }
      } catch (e) {
        this.error = e && e.message ? e.message : "Something went wrong.";
      } finally {
        this.loading = false;
      }
    },

    async fetchSurvey() {
      const { data } = await this.$api.get(this.surveyUrl());
      this.survey = data.survey;
      this.hasVoted = !!data.has_voted;
      this.allowRevoting = !!data.allow_revoting;
    },

    async fetchSchema() {
      // only choice/rating questions are exposed by the backend
      const { data } = await this.$api.get(this.schemaUrl());
      this.questions = (data.questions || []).map(q => ({
        id: q.id,
        text: q.text,
        help_text: q.help_text,
        multiple: !!q.multiple,
        type_field: q.type_field,
        choices: q.choices.map(c => ({ id: c.id, text: c.text })),
      }));
      this.questions.forEach(q => { this.$set(this.answers, q.id, q.multiple ? [] : null); });
    },

    async fetchResults() {
      try {
        const { data } = await this.$api.get(this.resultsUrl());
        this.results = data;
        this.$nextTick(this.renderAllCharts);
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.error = "Results are visible after you vote.";
        } else {
          throw e;
        }
      }
    },

    // -------- Vote --------
    normalizeAnswers() {
      const out = [];
      this.questions.forEach(q => {
        const raw = this.answers[q.id];
        if (raw == null || (Array.isArray(raw) && raw.length === 0)) return;
        const arr = Array.isArray(raw) ? raw.map(Number) : [Number(raw)];
        out.push({ question: q.id, choices: arr });
      });
      return { answers: out };
    },

    async submitVote() {
      const payload = this.normalizeAnswers();
      if (payload.answers.length === 0) {
        alert("Please choose at least one option.");
        return;
      }
      try {
        await this.$api.post(this.voteUrl(), payload);
        this.hasVoted = true;
        this.revoting = false;
        await this.fetchResults();
      } catch (e) {
        const msg = e.response?.data?.detail || "Could not submit vote.";
        alert(msg);
      }
    },

    startRevote() { this.revoting = true; },
    cancelRevote() { this.revoting = false; },

    // -------- Charts (Chart.js pie) --------
    swatchStyle(qid, cid) {
      return { width: "0.9rem", height: "0.9rem", backgroundColor: this.colorFor(qid, cid), display: "inline-block" };
    },

    renderAllCharts() {
      if (!this.results || !this.results.questions) return;

      // Wenn der Container beim ersten Render unsichtbar war (Tab/Collapse),
      // rendere nach einem Frame erneut.
      requestAnimationFrame(() => {
        this.results.questions.forEach(q => this.renderPie(q));
      });
    },

    renderPie(q) {
      const el = this.$refs['chart-' + q.id];
      const canvas = Array.isArray(el) ? el[0] : el;
      if (!canvas) return;

      // ggf. alten Chart zerstören
      if (this.charts && this.charts[q.id]) {
        this.charts[q.id].destroy();
        this.$delete(this.charts, q.id);
      }

      // Sicherstellen, dass die Summe > 0 ist (sonst sieht man nichts)
      const votes = q.choices.map(c => Number(c.votes || 0));
      const sum = votes.reduce((a, b) => a + b, 0);
      const data = sum > 0 ? votes : q.choices.map(() => 1);  // Fallback: gleichmäßiger Dummy
      const labels = q.choices.map(c => c.text);
      const bg = q.choices.map(c => this.colorFor(q.id, c.id));

      const chart = new Chart(canvas.getContext('2d'), {
        type: 'pie',
        data: { labels, datasets: [{ data, backgroundColor: bg }] },
        options: {
          responsive: true,
          maintainAspectRatio: false,    // nutzt die 300px Höhe des Wrappers
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const c = q.choices[ctx.dataIndex];
                  // Wenn wir im Dummy-Fall sind, trotzdem sinnvolle Anzeige:
                  if (sum === 0) return `${c.text}: 0% (0)`;
                  const p = (100 * (Number(c.votes || 0) / sum)).toFixed(2);
                  return `${c.text}: ${p}% (${c.votes})`;
                }
              }
            }
          }
        }
      });

      if (!this.charts) this.charts = {};
      this.$set(this.charts, q.id, chart);
    },

    // Farben wie gehabt
    colorFor(qid, cid) {
      if (!this.colorCache[qid]) this.$set(this.colorCache, qid, {});
      const map = this.colorCache[qid];
      if (!map[cid]) {
        const idx = Object.keys(map).length;
        map[cid] = this.hslColor(idx);
      }
      return map[cid];
    },
    hslColor(i, s = 70, l = 55) {
      const hue = Math.floor((i * 137.508) % 360);
      return `hsl(${hue}, ${s}%, ${l}%)`;
    },



  },

  beforeDestroy() {
    Object.values(this.charts).forEach(ch => ch && ch.destroy());
  }
};
</script>

<template>
  <div class="card shadow-sm">
    <div class="card-body">
      <h5 v-if="survey" class="card-title mb-3 pb-0">{{ survey.title }}</h5>
      <p v-if="survey" class="small text-muted">{{ survey.description }}</p>

      <div v-if="loading" class="text-muted">Loading…</div>
      <div v-else-if="error" class="alert alert-danger py-2">{{ error }}</div>

      <!-- RESULTS -->
      <div v-else-if="hasVoted && !revoting">
        <p class="text-muted small mb-3">
          You’ve voted.
          <button v-if="allowRevoting" type="button" class="btn bg-secondary btn-sm" @click="startRevote">
            Revote
          </button>
        </p>

        <p v-if="results" class="mb-3">
          <span class="badge bg-light text-dark">Total responses: {{ results.total_responses }}</span>
        </p>

        <div v-if="results" class="row g-4">
          <div v-for="q in results.questions" :key="q.id" class="col-12 col-md-6">
            <div class="border rounded p-3 h-100">
              <h6 class="mb-3">{{ q.text }}</h6>
              <div class="chart-wrap mb-3">
                <canvas :ref="'chart-'+q.id"></canvas>
              </div>
              <ul class="list-group list-group-flush small">
                <li v-for="c in q.choices" :key="c.id" class="list-group-item d-flex align-items-center">
                  <span class="d-inline-block rounded-circle me-2" :style="swatchStyle(q.id, c.id)"></span>
                  <span class="flex-grow-1">{{ c.text }}</span>
                  <span class="ms-2 text-muted">{{ c.percent }}% ({{ c.votes }})</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <button v-else type="button" class="btn btn-outline-secondary mt-2" @click="fetchResults">
          Show results
        </button>
      </div>

      <!-- VOTE FORM -->
      <form v-else @submit.prevent="submitVote" class="mt-2">
        <div v-if="questions.length">
          <div v-for="q in questions" :key="q.id" class="mb-4">

            <vote-rating v-if="q.type_field == type_rating"
              v-model="answers[q.id]"
              :label="q.text"
              :help="q.help_text"
              :max="q.max_stars || (q.choices && q.choices.length) || 5"
            />

            <!-- Single-/Multi-Choice -->
            <vote-question v-else
              :question="q"
              v-model="answers[q.id]"
            />
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary">Vote</button>
            <button
              v-if="hasVoted && allowRevoting"
              type="button"
              class="btn btn-outline-secondary"
              @click="cancelRevote"
            >Cancel</button>
          </div>
        </div>

        <p v-else class="text-muted small">Voting form isn’t available yet.</p>
      </form>
    </div>
  </div>
</template>

<style scoped>
.chart-wrap { position: relative; height: 300px; }  /* feste Höhe */
canvas { width: 100%; height: 100%; }       
</style>
